import {
  FilterGroup,
  HeaderConfig,
  Row,
} from 'components/Table/KhulaSupplierTable';
import {
  IFarmInputPurchaseOrder,
  IGenericType,
  IQuoteSupportingDocumentInput,
} from 'constants/interfaces';
import moment from 'moment';
import {
  _NumberWithSpaces,
  IPurchaseOrderStatus,
  EPurchaseOrderSupportingDocs,
  VAT,
} from 'constants/index';
import React, {useMemo, useState} from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from '@material-ui/core';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import {
  apiGetPurchaseOrder,
  apiSavePurchaseOrderSupportingDocument,
  apiUpdatePurchaseOrderStatus,
} from 'constants/Api';
import {reduxUpdatePurchaseOrder} from 'appredux/actions';
import {AddOrUpdateSupportingDocumentDialog} from 'components/SupportingDocuments/SupportingDocuments';

export const purchaseOrderTableHeaders = (): HeaderConfig[] => [
  {
    field: 'formattedId',
    title: 'Purchase Order',
    isSortable: true,
    sortField: 'id',
    isSearchable: true,
    searchField: 'id',
    isRightAligned: true,
  },
  {
    field: 'days',
    title: 'Days',
    isSortable: true,
    isSearchable: true,
    isRightAligned: true,
  },
  {
    field: 'formattedDate',
    title: 'Date',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'mainSupplierName',
    title: 'Supplier',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'depotName',
    title: 'Depot',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'farmerFullName',
    title: 'Farmer',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'purchaseOrderStatusSelect',
    title: 'Progress',
    isSortable: true,
    sortField: 'currStatusName',
    isSearchable: true,
    searchField: 'currStatusName',
    isRightAligned: false,
  },
  {
    field: 'formattedTotal',
    title: 'Total (R)',
    sortField: 'totalExclVat',
    isSortable: true,
    isSearchable: false,
    isRightAligned: true,
    noWrap: true,
  },
  {
    field: 'actions',
    title: '',
    isSortable: false,
    isSearchable: false,
    isRightAligned: true,
  },
];

export function formattedPurchaseOrderId(order: IFarmInputPurchaseOrder) {
  return `KHULA-${order.id}`;
}

export function purchaseOrderTotal(order: IFarmInputPurchaseOrder) {
  if (order.invoice.vatExempt) return order.total;

  // remove VAT from each line item
  const lineItemsExclVat = order?.orders?.map((o) => ({
    ...o,
    price: o.price / (1 + VAT),
  }));

  const lineItemTotal = lineItemsExclVat?.reduce(
    (prev, curr) => prev + Number(curr.price) * Number(curr.quantity),
    0,
  );
  return lineItemTotal + (order?.invoice?.logisticsCost || 0);
}

export const purchaseOrderTableRows = (
  orders: IFarmInputPurchaseOrder[],
  statuses: IGenericType[],
  onSelect: (order: IFarmInputPurchaseOrder) => any,
): Row[] => {
  return orders.map((order) => {
    // Provide a numeric or string `id` for Row
    const rowId = String(order.id);

    // Days since created
    const daysSinceCreated = moment().diff(
      new Date(parseInt(order.createdDate)),
      'days',
    );

    // Current status object (for searching/sorting)
    const currStatus = getCurrentPurchaseOrderStatus(order, statuses);
    const currStatusName = currStatus?.name || '';

    // totalExclVat for sorting
    const totalExclVat = purchaseOrderTotal(order);

    return {
      /** Row identification */
      id: rowId,

      /** Additional columns as strings/numbers/ReactNodes */
      formattedId: formattedPurchaseOrderId(order),
      days: daysSinceCreated,
      formattedDate: moment(new Date(parseInt(order.createdDate))).format(
        'YYYY-MM-DD',
      ),
      mainSupplierName:
        order?.mainSupplier?.name ||
        order.invoice.depot?.mainSupplier?.name ||
        '',
      depotName: order.invoice.depot?.name || '',
      farmerFullName: `${order.invoice.farmer.name} ${order.invoice.farmer.surname}`,
      // this text is used for the table's sortField/searchField
      currStatusName, // must be string or something that can be sorted
      totalExclVat,

      // The actual ReactNode for the "Progress" column
      purchaseOrderStatusSelect: (
        <PurchaseOrderStatusSelect
          purchaseOrder={order}
          purchaseOrderStatuses={statuses}
        />
      ),

      // A formatted total as a string/React fragment
      formattedTotal: 'R ' + _NumberWithSpaces(totalExclVat ?? 0),

      // The actions cell with icon
      actions: <PurchaseOrderRowIcons onSelect={() => onSelect(order)} />,
    };
  });
};

function PurchaseOrderRowIcons(props: {onSelect: () => any}) {
  return (
    <Box display="flex" flexDirection="row">
      <Tooltip title="View purchase order" aria-label="View purchase order">
        <IconButton
          aria-label="view purchase order"
          size="small"
          onClick={props.onSelect}
        >
          <ChatBubbleOutlineIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function PurchaseOrderStatusSelect(props: {
  purchaseOrder: IFarmInputPurchaseOrder;
  purchaseOrderStatuses: IGenericType[];
}) {
  const currStatus = useMemo(() => {
    return getCurrentPurchaseOrderStatus(
      props.purchaseOrder,
      props.purchaseOrderStatuses,
    );
  }, [props.purchaseOrder, props.purchaseOrderStatuses]);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isDeliveryNoteUploadOpen, setIsDeliveryNoteUploadOpen] =
    useState(false);
  const [isUploadingDeliveryNote, setIsUploadingDeliveryNote] = useState(false);
  const [isInvoiceUploadOpen, setIsInvoiceUploadOpen] = useState(false);
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
  const [statusId, setStatusId] = useState<number>(currStatus?.id);

  const handleChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const newStatusId = parseInt(event.target.value as string);
    setStatusId(newStatusId);
    if (newStatusId === IPurchaseOrderStatus.Delivered) {
      setIsDeliveryNoteUploadOpen(true);
    } else if (newStatusId === IPurchaseOrderStatus.Invoiced) {
      setIsInvoiceUploadOpen(true);
    } else {
      setIsConfirmationOpen(true);
    }
  };

  const handleAccept = async () => {
    apiUpdatePurchaseOrderStatus(props.purchaseOrder.id, statusId)
      .then((updatedPurchaseOrder) =>
        reduxUpdatePurchaseOrder(updatedPurchaseOrder),
      )
      .catch((error) => {
        alert('Error updating order status');
        console.error('Error updating order status: ', error);
        setStatusId(currStatus?.id);
      })
      .finally(() => setIsConfirmationOpen(false));
  };

  const handleCancel = () => {
    setIsConfirmationOpen(false);
    setIsDeliveryNoteUploadOpen(false);
    setIsInvoiceUploadOpen(false);
    setStatusId(currStatus?.id);
  };

  const handleDeliveryNoteUpload = async (
    doc: IQuoteSupportingDocumentInput,
  ) => {
    setIsUploadingDeliveryNote(true);
    return apiSavePurchaseOrderSupportingDocument({
      ...doc,
      purchaseOrderId: props.purchaseOrder.id,
      title: EPurchaseOrderSupportingDocs.DeliveryNote,
    })
      .then(handleAccept)
      .catch((error) => {
        alert('Error updating order status');
        console.error('Error updating order status: ', error);
        setStatusId(currStatus?.id);
      })
      .finally(() => setIsUploadingDeliveryNote(false));
  };

  const handleInvoiceUpload = async (doc: IQuoteSupportingDocumentInput) => {
    setIsUploadingInvoice(true);
    return apiSavePurchaseOrderSupportingDocument({
      ...doc,
      purchaseOrderId: props.purchaseOrder.id,
      title: EPurchaseOrderSupportingDocs.Invoice,
    })
      .then(() => apiGetPurchaseOrder({id: props.purchaseOrder.id}))
      .then(reduxUpdatePurchaseOrder)
      .then(handleAccept)
      .catch((error) => {
        alert('Error updating order status');
        console.error('Error updating order status: ', error);
        setStatusId(currStatus?.id);
      })
      .finally(() => setIsUploadingInvoice(false));
  };

  const findStatus = (id: number) => {
    return props.purchaseOrderStatuses.find((s) => s.id === id);
  };

  return (
    <div>
      <Select
        className="purchase-order-status-select"
        value={statusId}
        onChange={handleChange}
        renderValue={(val) => {
          const st = findStatus(val as number);
          return (
            <Chip
              label={st?.name || ''}
              size="small"
              className={getPurchaseOrderStatusIndicator(
                props.purchaseOrder,
                st,
              )}
            />
          );
        }}
      >
        {props.purchaseOrderStatuses.map((status) => (
          <MenuItem key={status.id} value={status.id}>
            {status.name}
          </MenuItem>
        ))}
      </Select>

      <Dialog
        className="order-confirmation-dialog"
        disableEscapeKeyDown
        open={isConfirmationOpen}
        onClose={handleCancel}
      >
        <DialogTitle>
          Update Order Status: {formattedPurchaseOrderId(props.purchaseOrder)}
        </DialogTitle>
        <DialogContent className="body">
          Are you sure you want to update the purchase order status?
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className="accept-button"
            onClick={handleAccept}
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delivery Note upload */}
      <AddOrUpdateSupportingDocumentDialog
        open={isDeliveryNoteUploadOpen}
        isLoading={isUploadingDeliveryNote}
        document={{title: EPurchaseOrderSupportingDocs.DeliveryNote}}
        onUpdate={handleDeliveryNoteUpload}
        onCancel={handleCancel}
        titleOptions={[EPurchaseOrderSupportingDocs.DeliveryNote]}
      />

      {/* Invoice upload */}
      <AddOrUpdateSupportingDocumentDialog
        open={isInvoiceUploadOpen}
        isLoading={isUploadingInvoice}
        document={{title: EPurchaseOrderSupportingDocs.Invoice}}
        onUpdate={handleInvoiceUpload}
        onCancel={handleCancel}
        titleOptions={[EPurchaseOrderSupportingDocs.Invoice]}
      />
    </div>
  );
}

function getPurchaseOrderStatusIndicator(
  order: IFarmInputPurchaseOrder,
  currStatus?: IGenericType,
): 'default' | 'warning' | 'error' | 'success' {
  const days = moment().diff(new Date(parseInt(order.createdDate)), 'days');

  const successStates = [
    IPurchaseOrderStatus.Delivered,
    IPurchaseOrderStatus.Invoiced,
    IPurchaseOrderStatus.Paid,
  ];

  const statusId = parseInt(currStatus?.id);

  if (successStates.includes(statusId)) return 'success';
  if (days > 7) return 'error';
  if (
    (days > 1 && statusId === IPurchaseOrderStatus.Unconfirmed) ||
    statusId === IPurchaseOrderStatus.NoStock ||
    (days > 3 && statusId < IPurchaseOrderStatus.CourierCollectionArranged)
  ) {
    return 'warning';
  }
  return 'default';
}

function getCurrentPurchaseOrderStatus(
  order: IFarmInputPurchaseOrder,
  purchaseOrderStatuses: IGenericType[],
): IGenericType | undefined {
  const statusUpdates = order.statusUpdates.sort(
    (a, b) => parseInt(a.date) - parseInt(b.date),
  );
  if (statusUpdates.length === 0) {
    return purchaseOrderStatuses.sort(
      (a, b) => parseInt(a.id) - parseInt(b.id),
    )[0];
  }
  return statusUpdates[statusUpdates.length - 1].status;
}

/** Filter groups for status */
const statusFilterGroup: FilterGroup = {
  name: 'Status',
  filters: [
    {
      field: 'statusIndicator',
      value: 'default',
      title: 'Default',
    },
    {
      field: 'statusIndicator',
      value: 'warning',
      title: 'Warning',
    },
    {
      field: 'statusIndicator',
      value: 'error',
      title: 'Failure',
    },
    {
      field: 'statusIndicator',
      value: 'success',
      title: 'Success',
    },
  ],
};

export const purchaseOrderTableFilters = [statusFilterGroup];
